import React from 'react';
import './Reports.scss';
import DateFromTo from '../../../widgets/DateFromTo/DateFromTo';
import DataTable from '../Table/Datatable';
import useAdminUser from '../../../data/useAdminUser';

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DOMAIN_API_HOST } from '../../../configs/main';

const PAGE_SIZE = 10;
const CLOSING_REPORTS_PAGE_SIZE = 20;
const ORD_REPORTS_PAGE_SIZE = 10;
const MIN_CLOSING_REPORT_YEAR = 2026;

const MONTH_OPTIONS = [
  { value: 1, label: 'Январь' },
  { value: 2, label: 'Февраль' },
  { value: 3, label: 'Март' },
  { value: 4, label: 'Апрель' },
  { value: 5, label: 'Май' },
  { value: 6, label: 'Июнь' },
  { value: 7, label: 'Июль' },
  { value: 8, label: 'Август' },
  { value: 9, label: 'Сентябрь' },
  { value: 10, label: 'Октябрь' },
  { value: 11, label: 'Ноябрь' },
  { value: 12, label: 'Декабрь' },
];

const parseFilenameFromContentDisposition = (value) => {
  if (!value) return null;
  const matchStar = value.match(/filename\*\s*=\s*([^;]+)/i);
  if (matchStar?.[1]) {
    const raw = matchStar[1].trim();
    const cleaned = raw.replace(/^utf-8''/i, '').replace(/^"|"$/g, '');
    try {
      return decodeURIComponent(cleaned);
    } catch {
      return cleaned;
    }
  }
  const match = value.match(/filename\s*=\s*([^;]+)/i);
  return match?.[1] ? match[1].trim().replace(/^"|"$/g, '') : null;
};

const toISODate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(d.getDate()).padStart(2, '0')}`;
};

const columns = [
  {
    Header: 'Почта клиента',
    accessor: 'email',
    maxWidth: 400,
    minWidth: 120,
    width: 220,
  },
  {
    Header: 'Дата последнего списания',
    accessor: 'lastDebitDate',
    maxWidth: 400,
    minWidth: 140,
    width: 200,
  },
  {
    Header: 'Дата последнего пополнения',
    accessor: 'lastTopUpDate',
    maxWidth: 400,
    minWidth: 140,
    width: 200,
  },
  {
    Header: 'Сумма последнего пополнения',
    accessor: 'lastTopUpAmount',
    maxWidth: 400,
    minWidth: 140,
    width: 220,
  },
  {
    Header: 'Суточный лимит',
    accessor: 'dailyLimit',
    maxWidth: 400,
    minWidth: 120,
    width: 160,
  },
  {
    Header: 'Среднее списание за 3 дня',
    accessor: 'avgDebit3Days',
    maxWidth: 400,
    minWidth: 160,
    width: 220,
  },
  {
    Header: 'Ставка',
    accessor: 'rate',
    maxWidth: 400,
    minWidth: 140,
    width: 180,
  },
  {
    Header: 'Тип оплаты',
    accessor: 'paymentType',
    maxWidth: 400,
    minWidth: 140,
    width: 180,
  },
  {
    Header: 'Текущий баланс',
    accessor: 'currentBalance',
    maxWidth: 400,
    minWidth: 140,
    width: 180,
  },
  {
    Header: 'Планируемая сумма пополнений',
    accessor: 'plannedTopUpAmount',
    maxWidth: 400,
    minWidth: 180,
    width: 240,
  },
  {
    Header: 'Планируемая дата',
    accessor: 'plannedTopUpDate',
    maxWidth: 400,
    minWidth: 140,
    width: 180,
  },
];

function Report({ mode }) {
  const { role, token } = useAdminUser();
  const isSuperuser = role === 'SUPERUSER';
  const canAccessClosingDocuments = role === 'SUPERUSER' || role === 'ADMIN';
  const isClosingDocumentsMode = mode === 'closing-documents';

  const [brand, setBrand] = React.useState('programmatic');
  const [dateRange, setDateRange] = React.useState([null, null]);
  const [selectedManager, setSelectedManager] = React.useState('');
  const [selectedFilters, setSelectedFilters] = React.useState([]);
  const [managers, setManagers] = React.useState([]);

  const [rows, setRows] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const now = React.useMemo(() => new Date(), []);
  const initialClosingDate = React.useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, [now]);
  const [closingMonth, setClosingMonth] = React.useState(
    initialClosingDate.month
  );
  const [closingYear, setClosingYear] = React.useState(
    Math.max(MIN_CLOSING_REPORT_YEAR, initialClosingDate.year)
  );
  const [closingRows, setClosingRows] = React.useState([]);
  const [closingTotalPages, setClosingTotalPages] = React.useState(0);
  const [closingTotalRecords, setClosingTotalRecords] = React.useState(0);
  const [closingPage, setClosingPage] = React.useState(1);
  const [closingLoading, setClosingLoading] = React.useState(false);
  const [closingError, setClosingError] = React.useState(null);
  const [downloadingReportId, setDownloadingReportId] = React.useState(null);
  const [exportingClosing, setExportingClosing] = React.useState(false);
  const [exportingOrd, setExportingOrd] = React.useState(false);

  const [ordRows, setOrdRows] = React.useState([]);
  const [ordTotalPages, setOrdTotalPages] = React.useState(0);
  const [ordTotalRecords, setOrdTotalRecords] = React.useState(0);
  const [ordPage, setOrdPage] = React.useState(1);
  const [ordLoading, setOrdLoading] = React.useState(false);
  const [ordError, setOrdError] = React.useState(null);
  const [ordActiveTab, setOrdActiveTab] = React.useState('closing');

  React.useEffect(() => {
    if (!isSuperuser || isClosingDocumentsMode) return;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    };
    fetch(`${DOMAIN_API_HOST}/api/v1/admin/employee`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    }).then((res) => {
      if (res.ok) res.json().then((data) => setManagers(data));
    });
  }, [isSuperuser, token, isClosingDocumentsMode]);

  const buildParams = React.useCallback(
    (extraParams = {}) => {
      const [start, end] = dateRange;
      const isScoreLower300 = selectedFilters.includes('lowBalance');
      const isChargeLess3DayLimit = selectedFilters.includes(
        'chargeLess3DayLimit'
      );
      const isScoreGreater300 = selectedFilters.includes('balanceGreater300');
      const isAgencyClickru = selectedFilters.includes('agencyClickru');
      const params = new URLSearchParams();
      params.set('brand', brand);
      params.set('is_score_lower_300', isScoreLower300);
      params.set('is_charge_less_3day_limit', isChargeLess3DayLimit);
      params.set('is_score_greater_300', isScoreGreater300);
      if (isAgencyClickru) params.set('agency', 'clickru');
      if (start) params.set('date_from', toISODate(start));
      if (end) params.set('date_to', toISODate(end));
      if (selectedManager) params.set('user_id', selectedManager);
      Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));
      return params;
    },
    [brand, dateRange, selectedFilters, selectedManager]
  );

  React.useEffect(() => {
    if (isClosingDocumentsMode) return;
    const [start, end] = dateRange;
    if (!start || !end) return;

    setLoading(true);
    setError(null);

    const params = buildParams({ page, limit: PAGE_SIZE });

    fetch(`${DOMAIN_API_HOST}/api/v1/admin/client-balance-report?${params}`, {
      headers: { Authorization: 'Bearer ' + token },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Ошибка ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setRows(data.data || []);
        setTotalPages(data.total_pages || 0);
        setTotalRecords(data.total_records || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [
    brand,
    dateRange,
    selectedManager,
    page,
    token,
    buildParams,
    isClosingDocumentsMode,
  ]);

  React.useEffect(() => {
    if (isClosingDocumentsMode) return;
    setPage(1);
  }, [
    brand,
    dateRange,
    selectedFilters,
    selectedManager,
    isClosingDocumentsMode,
  ]);

  const handleFiltersChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedFilters((prev) => {
      let next = typeof value === 'string' ? value.split(',') : value;

      if (next.includes('lowBalance') && next.includes('balanceGreater300')) {
        const added = next.find((v) => !prev.includes(v));
        if (added === 'lowBalance') {
          next = next.filter((v) => v !== 'balanceGreater300');
        } else if (added === 'balanceGreater300') {
          next = next.filter((v) => v !== 'lowBalance');
        } else {
          next = next.filter((v) => v !== 'balanceGreater300');
        }
      }

      return next;
    });
  };

  const handleExport = async () => {
    const [start, end] = dateRange;
    if (!start || !end) return;
    const params = buildParams();
    try {
      const response = await fetch(
        `${DOMAIN_API_HOST}/api/v1/admin/client-balance-report/export?${params}`,
        {
          method: 'GET',
          headers: { Authorization: 'Bearer ' + token },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers.get('content-disposition');
        let filename = `client_report.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Ошибка при скачивании файла:', response.statusText);
      }
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
    }
  };

  const handleDateChange = (value) => {
    setDateRange(value || [null, null]);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ru-RU');
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toLocaleString('ru-RU');
  };

  const renderFiltersValue = (selected) => {
    if (selected.length === 0) {
      return 'Без фильтров';
    }
    const labelsMap = {
      lowBalance: 'Баланс ≤ 300 ₽',
      balanceGreater300: 'Баланс > 300 ₽',
      chargeLess3DayLimit: 'Списания за 3 дня < 3x суточных лимитов',
      agencyClickru: 'Только clickru',
    };
    return selected.map((key) => labelsMap[key]).join(', ');
  };

  const tableData = React.useMemo(
    () =>
      rows.map((row) => ({
        email: row.email,
        lastDebitDate: formatDate(row.date_of_last_charge),
        lastTopUpDate: formatDate(row.date_of_last_deposit),
        lastTopUpAmount:
          row.amount_of_last_deposit != null
            ? `${formatNumber(row.amount_of_last_deposit)} ₽`
            : '-',
        dailyLimit:
          row.day_limit != null ? `${formatNumber(row.day_limit)} ₽` : '-',
        avgDebit3Days:
          row.avg_charge != null ? `${formatNumber(row.avg_charge)} ₽` : '-',
        rate: row.fix_price != null ? `${formatNumber(row.fix_price)} ₽` : '-',
        paymentType: row.target_type || '-',
        currentBalance:
          row.current_balance != null
            ? `${formatNumber(row.current_balance)} ₽`
            : '-',
        plannedTopUpAmount:
          row.plan_deposit_amount != null
            ? `${formatNumber(row.plan_deposit_amount)} ₽`
            : '-',
        plannedTopUpDate: formatDate(row.plan_deposit_date),
      })),
    [rows]
  );

  const closingYears = React.useMemo(() => {
    const currentYear = now.getFullYear();
    const endYear = Math.max(currentYear + 2, MIN_CLOSING_REPORT_YEAR);
    const length = endYear - MIN_CLOSING_REPORT_YEAR + 1;
    return Array.from(
      { length },
      (_, index) => MIN_CLOSING_REPORT_YEAR + index
    );
  }, [now]);

  React.useEffect(() => {
    if (!isClosingDocumentsMode || !canAccessClosingDocuments) return;

    let cancelled = false;
    setClosingLoading(true);
    setClosingError(null);

    const params = new URLSearchParams({
      page: String(closingPage),
      per_page: String(CLOSING_REPORTS_PAGE_SIZE),
      month: String(closingMonth),
      year: String(closingYear),
    });

    fetch(
      `${DOMAIN_API_HOST}/api/v1/payment/closing-reports/admin?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Ошибка ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setClosingRows(data.data || []);
        setClosingTotalPages(data.total_pages || 0);
        setClosingTotalRecords(data.total_records || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setClosingRows([]);
        setClosingError(err.message || 'Не удалось загрузить закрывашки');
      })
      .finally(() => {
        if (!cancelled) setClosingLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isClosingDocumentsMode,
    canAccessClosingDocuments,
    token,
    closingPage,
    closingMonth,
    closingYear,
  ]);

  React.useEffect(() => {
    if (!isClosingDocumentsMode) return;
    setClosingPage(1);
    setOrdPage(1);
  }, [closingMonth, closingYear, isClosingDocumentsMode]);

  React.useEffect(() => {
    if (
      !isClosingDocumentsMode ||
      !canAccessClosingDocuments ||
      ordActiveTab !== 'ord'
    )
      return;

    let cancelled = false;
    setOrdLoading(true);
    setOrdError(null);

    const params = new URLSearchParams({
      page: String(ordPage),
      per_page: String(ORD_REPORTS_PAGE_SIZE),
      month: String(closingMonth),
      year: String(closingYear),
    });

    fetch(
      `${DOMAIN_API_HOST}/api/v1/payment/ord-reports?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Ошибка ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setOrdRows(data.data || []);
        setOrdTotalPages(data.total_pages || 0);
        setOrdTotalRecords(data.total_records || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrdRows([]);
        setOrdError(err.message || 'Не удалось загрузить отчёт ОРД');
      })
      .finally(() => {
        if (!cancelled) setOrdLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isClosingDocumentsMode,
    canAccessClosingDocuments,
    token,
    ordPage,
    closingMonth,
    closingYear,
    ordActiveTab,
  ]);

  const downloadClosingDocument = React.useCallback(
    async (reportId) => {
      if (!reportId) return;
      setDownloadingReportId(reportId);
      try {
        const response = await fetch(
          `${DOMAIN_API_HOST}/api/v1/payment/closing-reports/${reportId}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) throw new Error(`Ошибка ${response.status}`);

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename =
          parseFilenameFromContentDisposition(
            response.headers.get('content-disposition')
          ) || 'closing-report.pdf';
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        setClosingError(e.message || 'Не удалось скачать документ');
      } finally {
        setDownloadingReportId(null);
      }
    },
    [token]
  );

  const exportClosingReports = React.useCallback(async () => {
    setExportingClosing(true);
    try {
      const params = new URLSearchParams({
        month: String(closingMonth),
        year: String(closingYear),
      });
      const response = await fetch(
        `${DOMAIN_API_HOST}/api/v1/payment/closing-reports/admin/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error(`Ошибка ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename =
        parseFilenameFromContentDisposition(
          response.headers.get('content-disposition')
        ) ||
        `closing_reports_${closingYear}_${String(closingMonth).padStart(
          2,
          '0'
        )}.xlsx`;
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setClosingError(e.message || 'Не удалось выгрузить xlsx');
    } finally {
      setExportingClosing(false);
    }
  }, [token, closingMonth, closingYear]);

  const exportOrdReports = React.useCallback(async () => {
    setExportingOrd(true);
    try {
      const params = new URLSearchParams({
        page: String(ordPage),
        per_page: String(ORD_REPORTS_PAGE_SIZE),
        month: String(closingMonth),
        year: String(closingYear),
      });
      const response = await fetch(
        `${DOMAIN_API_HOST}/api/v1/payment/ord-reports/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error(`Ошибка ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename =
        parseFilenameFromContentDisposition(
          response.headers.get('content-disposition')
        ) ||
        `ord_reports_${closingYear}_${String(closingMonth).padStart(
          2,
          '0'
        )}_page_${ordPage}.xlsx`;
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setOrdError(e.message || 'Не удалось выгрузить xlsx');
    } finally {
      setExportingOrd(false);
    }
  }, [token, closingMonth, closingYear, ordPage]);

  const closingColumns = React.useMemo(
    () => [
      {
        Header: 'Полное наименование юр лица',
        accessor: 'fullName',
        width: 450,
        minWidth: 220,
        maxWidth: 700,
      },
      {
        Header: 'Почта клиента',
        accessor: 'clientEmail',
        width: 230,
        minWidth: 180,
        maxWidth: 380,
      },
      {
        Header: 'Период',
        accessor: 'period',
        width: 120,
        minWidth: 100,
        maxWidth: 160,
      },
      {
        Header: 'Номер документа',
        accessor: 'documentNumber',
        width: 220,
        minWidth: 160,
        maxWidth: 340,
      },
      {
        Header: 'Дата формирования документа',
        accessor: 'documentDate',
        width: 180,
        minWidth: 150,
        maxWidth: 240,
      },
      {
        Header: 'Потраченная сумма за период с НДС',
        accessor: 'amountVat',
        width: 230,
        minWidth: 170,
        maxWidth: 300,
      },
      {
        Header: 'Ссылка на скачивание',
        accessor: 'downloadLink',
        width: 170,
        minWidth: 140,
        maxWidth: 220,
      },
    ],
    []
  );

  const ordColumns = React.useMemo(
    () => [
      {
        Header: 'Наименование юр лица',
        accessor: 'entityFullName',
        width: 500,
        minWidth: 200,
        maxWidth: 700,
      },
      {
        Header: 'Почта клиента',
        accessor: 'clientEmail',
        width: 220,
        minWidth: 180,
        maxWidth: 300,
      },
      {
        Header: 'Юр. лицо оплаты',
        accessor: 'legalEntity',
        width: 200,
        minWidth: 160,
        maxWidth: 300,
      },
      {
        Header: 'Дата регистрации в ЛК',
        accessor: 'registrationDate',
        width: 160,
        minWidth: 130,
        maxWidth: 200,
      },
      {
        Header: 'ЕРИД',
        accessor: 'erid',
        width: 160,
        minWidth: 120,
        maxWidth: 220,
      },
      {
        Header: 'Тип оплаты',
        accessor: 'paymentType',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        Header: 'Битвин',
        accessor: 'bitvin',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
      },
      {
        Header: 'Директ Адверт',
        accessor: 'directAdvert',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        Header: 'Бидвол',
        accessor: 'bidvol',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
      },
      {
        Header: 'Показы',
        accessor: 'shows',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        Header: 'Клики',
        accessor: 'clicks',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        Header: 'Сумма без НДС',
        accessor: 'amountWithoutVat',
        width: 140,
        minWidth: 110,
        maxWidth: 180,
      },
      {
        Header: 'Сумма с НДС',
        accessor: 'amountWithVat',
        width: 140,
        minWidth: 110,
        maxWidth: 180,
      },
    ],
    []
  );

  const ordTableData = React.useMemo(() => {
    const CLICK_DISTRIBUTION = {
      bitvin: 0.57,
      bidvol: 0.26,
      directAdvert: 0.17,
    };

    return ordRows.map((row) => {
      const isCpm = row.paymentType === 'СРМ';
      const shows = Number(row.shows) || 0;
      const clicks = Number(row.clicks) || 0;

      const bitvinShows = Number(row.bitvinShows) || 0;
      const bidvolShows = Number(row.bidvolShows) || 0;
      const directAdvertShows = Number(row.directAdvertShows) || 0;

      return {
        id: `${row.id}_${row.paymentType}_${row.erid}`,
        entityFullName: row.entityFullName || '-',
        clientEmail: row.clientEmail || '-',
        legalEntity: row.legalEntity || '-',
        registrationDate: row.registrationDate || '-',
        erid: row.erid || '-',
        paymentType: isCpm ? 'СРМ' : 'СРС',
        bitvin: isCpm ? formatNumber(bitvinShows) : formatNumber(bitvinShows),
        directAdvert: isCpm
          ? formatNumber(directAdvertShows)
          : formatNumber(directAdvertShows),
        bidvol: isCpm ? formatNumber(bidvolShows) : formatNumber(bidvolShows),
        shows: isCpm ? formatNumber(shows) : '',
        clicks: isCpm ? '' : formatNumber(clicks),
        amountWithoutVat: `${formatNumber(row.amountWithoutVat)} ₽`,
        amountWithVat: `${formatNumber(row.amountWithVat)} ₽`,
      };
    });
  }, [ordRows]);

  const closingTableData = React.useMemo(() => {
    const ell = (value) => {
      const s = value == null || value === '' ? '—' : String(value);
      return (
        <span
          className="closing-documents-cellEllipsis"
          title={s === '—' ? undefined : s}
        >
          {s}
        </span>
      );
    };
    return closingRows.map((row) => ({
      id: row.id,
      fullName: ell(row.fullName),
      clientEmail: ell(row.clientEmail),
      period: ell(row.period),
      documentNumber: ell(row.documentNumber),
      documentDate: ell(row.documentDate),
      amountVat: ell(row.amountVat),
      downloadLink: (
        <button
          type="button"
          onClick={() => downloadClosingDocument(row.id)}
          disabled={!row.id || downloadingReportId === row.id}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: 'none',
            backgroundColor:
              !row.id || downloadingReportId === row.id ? '#b0c4f9' : '#3F73F9',
            color: '#FFFFFF',
            fontSize: 13,
            cursor:
              !row.id || downloadingReportId === row.id
                ? 'not-allowed'
                : 'pointer',
          }}
        >
          {downloadingReportId === row.id ? 'Скачиваем...' : 'Скачать'}
        </button>
      ),
    }));
  }, [closingRows, downloadClosingDocument, downloadingReportId]);

  if (isClosingDocumentsMode) {
    return (
      <div
        className="reports closing-documents-table"
        style={{
          width: '100%',
          padding: '20px 0',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            marginBottom: 14,
            paddingRight: 20,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => setOrdActiveTab('closing')}
            style={{
              color: ordActiveTab === 'closing' ? '#3F73F9' : '#7a7a7a',
              fontSize: '15px',
              fontWeight: '500',
              borderBottom:
                ordActiveTab === 'closing'
                  ? '2px solid #3F73F9'
                  : '2px solid transparent',
              paddingBottom: 6,
              background: 'none',
              border: 'none',
              borderRadius: 0,
              cursor: 'pointer',
            }}
          >
            Закрывашки
          </button>
          <button
            type="button"
            onClick={() => setOrdActiveTab('ord')}
            style={{
              color: ordActiveTab === 'ord' ? '#3F73F9' : '#7a7a7a',
              fontSize: '15px',
              fontWeight: '500',
              borderBottom:
                ordActiveTab === 'ord'
                  ? '2px solid #3F73F9'
                  : '2px solid transparent',
              paddingBottom: 6,
              background: 'none',
              border: 'none',
              borderRadius: 0,
              cursor: 'pointer',
            }}
          >
            ОРД
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            paddingRight: 20,
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: 180 }}>
            <FormControl fullWidth size="small">
              <InputLabel
                id="closing-month-label"
                sx={{ lineHeight: 1, opacity: '80%', fontSize: 14 }}
              >
                Месяц
              </InputLabel>
              <Select
                labelId="closing-month-label"
                label="Месяц"
                value={closingMonth}
                onChange={(e) => setClosingMonth(Number(e.target.value))}
                style={{ height: 32, background: '#FFFFFF' }}
                sx={{
                  '& .css-1d3z3hw-MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                }}
              >
                {MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ width: 140 }}>
            <FormControl fullWidth size="small">
              <InputLabel
                id="closing-year-label"
                sx={{ lineHeight: 1, opacity: '80%', fontSize: 14 }}
              >
                Год
              </InputLabel>
              <Select
                labelId="closing-year-label"
                label="Год"
                value={closingYear}
                onChange={(e) => setClosingYear(Number(e.target.value))}
                style={{ height: 32, background: '#FFFFFF' }}
                sx={{
                  '& .css-1d3z3hw-MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                }}
              >
                {closingYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
            <button
              type="button"
              onClick={
                ordActiveTab === 'ord' ? exportOrdReports : exportClosingReports
              }
              disabled={
                ordActiveTab === 'ord' ? exportingOrd : exportingClosing
              }
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                backgroundColor:
                  (ordActiveTab === 'ord' ? exportingOrd : exportingClosing)
                    ? '#b0c4f9'
                    : '#3F73F9',
                color: '#FFFFFF',
                fontSize: 14,
                fontWeight: 600,
                cursor:
                  (ordActiveTab === 'ord' ? exportingOrd : exportingClosing)
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {(ordActiveTab === 'ord' ? exportingOrd : exportingClosing)
                ? 'Экспортируем...'
                : 'Экспорт'}
            </button>
          </div>
        </div>

        <div
          style={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            paddingRight: 20,
          }}
        >
          {ordActiveTab === 'closing'
            ? closingLoading && (
                <div
                  style={{
                    padding: '20px 0',
                    fontSize: 14,
                    color: '#777',
                    textAlign: 'center',
                  }}
                >
                  Загрузка...
                </div>
              )
            : ordLoading && (
                <div
                  style={{
                    padding: '20px 0',
                    fontSize: 14,
                    color: '#777',
                    textAlign: 'center',
                  }}
                >
                  Загрузка...
                </div>
              )}
          {ordActiveTab === 'closing'
            ? closingError && (
                <div
                  style={{
                    padding: '20px 0',
                    fontSize: 14,
                    color: '#e53935',
                    textAlign: 'center',
                  }}
                >
                  {closingError}
                </div>
              )
            : ordError && (
                <div
                  style={{
                    padding: '20px 0',
                    fontSize: 14,
                    color: '#e53935',
                    textAlign: 'center',
                  }}
                >
                  {ordError}
                </div>
              )}
          {ordActiveTab === 'closing'
            ? !closingLoading &&
              !closingError && (
                <>
                  <DataTable data={closingTableData} columns={closingColumns} />
                  {closingTableData.length === 0 && (
                    <div
                      style={{
                        padding: '20px 0',
                        fontSize: 14,
                        color: '#777',
                        textAlign: 'center',
                      }}
                    >
                      Нет данных за выбранный период
                    </div>
                  )}
                </>
              )
            : !ordLoading &&
              !ordError && (
                <>
                  <DataTable data={ordTableData} columns={ordColumns} />
                  {ordTableData.length === 0 && (
                    <div
                      style={{
                        padding: '20px 0',
                        fontSize: 14,
                        color: '#777',
                        textAlign: 'center',
                      }}
                    >
                      Нет данных за выбранный период
                    </div>
                  )}
                </>
              )}
        </div>

        {ordActiveTab === 'closing' &&
          !closingLoading &&
          !closingError &&
          closingTotalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 12,
                marginTop: 16,
                paddingRight: 20,
                flexShrink: 0,
                fontSize: 14,
                color: '#444',
              }}
            >
              <button
                type="button"
                onClick={() => setClosingPage((prev) => Math.max(1, prev - 1))}
                disabled={closingPage === 1}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  background: closingPage === 1 ? '#f5f5f5' : '#fff',
                  cursor: closingPage === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ←
              </button>
              <span>
                Стр. {closingPage} из {closingTotalPages} ({closingTotalRecords}{' '}
                записей)
              </span>
              <button
                type="button"
                onClick={() =>
                  setClosingPage((prev) =>
                    Math.min(closingTotalPages, prev + 1)
                  )
                }
                disabled={closingPage === closingTotalPages}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  background:
                    closingPage === closingTotalPages ? '#f5f5f5' : '#fff',
                  cursor:
                    closingPage === closingTotalPages
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                →
              </button>
            </div>
          )}

        {ordActiveTab === 'ord' &&
          !ordLoading &&
          !ordError &&
          ordTotalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 12,
                marginTop: 16,
                paddingRight: 20,
                flexShrink: 0,
                fontSize: 14,
                color: '#444',
              }}
            >
              <button
                type="button"
                onClick={() => setOrdPage((prev) => Math.max(1, prev - 1))}
                disabled={ordPage === 1}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  background: ordPage === 1 ? '#f5f5f5' : '#fff',
                  cursor: ordPage === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ←
              </button>
              <span>
                Стр. {ordPage} из {ordTotalPages} ({ordTotalRecords} записей)
              </span>
              <button
                type="button"
                onClick={() =>
                  setOrdPage((prev) => Math.min(ordTotalPages, prev + 1))
                }
                disabled={ordPage === ordTotalPages}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  background: ordPage === ordTotalPages ? '#f5f5f5' : '#fff',
                  cursor: ordPage === ordTotalPages ? 'not-allowed' : 'pointer',
                }}
              >
                →
              </button>
            </div>
          )}
      </div>
    );
  }

  return (
    <div
      className="reports"
      style={{ width: '100%', padding: '20px 0', height: '100%' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '12px',
          paddingRight: 20,
        }}
      >
        <div style={{ width: 320, flexShrink: 0, marginRight: 90 }}>
          <DateFromTo handleChange={handleDateChange} />
        </div>
        {isSuperuser && (
          <Box sx={{ width: 200, flexShrink: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel
                id="manager-select-label"
                sx={{ lineHeight: 1, opacity: '80%', fontSize: 14 }}
              >
                Менеджер
              </InputLabel>
              <Select
                labelId="manager-select-label"
                label="Менеджер"
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
                style={{ height: 32, background: '#FFFFFF' }}
                sx={{
                  '& .css-1d3z3hw-MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                }}
              >
                <MenuItem value="">Все менеджеры</MenuItem>
                {managers.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <Box sx={{ minWidth: 260, flexShrink: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel
              id="filters-select-label"
              sx={{ lineHeight: 1, opacity: '80%', fontSize: 14 }}
            >
              Фильтры
            </InputLabel>
            <Select
              labelId="filters-select-label"
              multiple
              value={selectedFilters}
              onChange={handleFiltersChange}
              label="Фильтры"
              renderValue={renderFiltersValue}
              style={{ height: 32, background: '#FFFFFF' }}
              sx={{
                '& .css-1d3z3hw-MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
              }}
            >
              <MenuItem value="lowBalance">
                <Checkbox
                  checked={selectedFilters.indexOf('lowBalance') > -1}
                />
                <ListItemText primary="Баланс ≤ 300 ₽" />
              </MenuItem>
              <MenuItem value="balanceGreater300">
                <Checkbox
                  checked={selectedFilters.indexOf('balanceGreater300') > -1}
                />
                <ListItemText primary="Баланс > 300 ₽" />
              </MenuItem>
              <MenuItem value="chargeLess3DayLimit">
                <Checkbox
                  checked={selectedFilters.indexOf('chargeLess3DayLimit') > -1}
                />
                <ListItemText primary="Списания за 3 дня < 3× суточного лимита" />
              </MenuItem>
              <MenuItem value="agencyClickru">
                <Checkbox
                  checked={selectedFilters.indexOf('agencyClickru') > -1}
                />
                <ListItemText primary="Только clickru" />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleExport}
            disabled={!dateRange[0] || !dateRange[1]}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              backgroundColor:
                !dateRange[0] || !dateRange[1] ? '#b0c4f9' : '#3F73F9',
              color: '#FFFFFF',
              fontSize: 14,
              fontWeight: 600,
              cursor:
                !dateRange[0] || !dateRange[1] ? 'not-allowed' : 'pointer',
            }}
          >
            Скачать отчёт
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '20px',
        }}
      >
        <button
          style={{
            color: brand === 'programmatic' ? '#3F73F9' : '#7a7a7a',
            fontSize: '14px',
            fontWeight: 600,
          }}
          onClick={() => setBrand('programmatic')}
        >
          Programmatic
        </button>
        <button
          style={{
            color: brand === 'retargeo' ? '#3F73F9' : '#7a7a7a',
            fontSize: '14px',
            fontWeight: 600,
          }}
          onClick={() => setBrand('retargeo')}
        >
          Retargeo
        </button>
      </div>

      <div
        style={{
          width: '100%',
          height: 'calc(100% - 110px)',
          overflowX: 'auto',
          overflowY: 'auto',
          paddingRight: 20,
        }}
      >
        {loading && (
          <div
            style={{
              padding: '20px 0',
              fontSize: 14,
              color: '#777',
              textAlign: 'center',
            }}
          >
            Загрузка...
          </div>
        )}
        {error && (
          <div
            style={{
              padding: '20px 0',
              fontSize: 14,
              color: '#e53935',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}
        {!loading && !error && (
          <>
            <DataTable data={tableData} columns={columns} />
            {tableData.length === 0 && (
              <div
                style={{
                  padding: '20px 0',
                  fontSize: 14,
                  color: '#777',
                  textAlign: 'center',
                }}
              >
                {!dateRange[0] || !dateRange[1]
                  ? 'Выберите период для загрузки данных'
                  : 'Нет данных за выбранный период'}
              </div>
            )}
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 12,
                  marginTop: 16,
                  fontSize: 14,
                  color: '#444',
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    background: page === 1 ? '#f5f5f5' : '#fff',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ←
                </button>
                <span>
                  Стр. {page} из {totalPages} ({totalRecords} записей)
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    background: page === totalPages ? '#f5f5f5' : '#fff',
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Report;
